import React from "react";
import { useAuthorization } from "@common/providers";
import {
  useV2GetProjectByIdQuery,
  useV2ListQuotesForAccountQuery,
} from "@generated";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import Show from "./Show/Show";
import { ThirdPartyService } from "./types/ThirdPartyService";

interface Props {
  project_id: string;
}

const ThirdPartyPricingV2Mspa: React.FC<Props> = ({ project_id }) => {
  const projectId = project_id;

  const { data: project, isLoading: projectLoading } = useV2GetProjectByIdQuery(
    {
      id: String(projectId),
    }
  );

  const { data: quotesData, isLoading: quotesLoading } =
    useV2ListQuotesForAccountQuery({
      filter: { project: projectId },
      include: ["vendor"],
      page: { size: 250 },
    });

  const authorized = useAuthorization();

  if (!authorized || projectLoading || quotesLoading || !project) {
    return <ScopeStackSpinner />;
  }

  const currencyUnit =
    (project?.data?.attributes as any)?.["field-labels"]?.currency_unit || "$";

  const vendors: Record<string, string> = {};
  if (quotesData?.included) {
    (quotesData.included as any[]).forEach((item: any) => {
      if (item.type === "vendors") {
        vendors[String(item.id)] = item.attributes?.name || "";
      }
    });
  }

  const services: ThirdPartyService[] =
    (quotesData?.data as any[])?.map((quote: any) => {
      const attr = quote.attributes;
      const vendorData = quote.relationships?.vendor?.data;
      const vendorName = vendorData ? vendors[String(vendorData.id)] || "" : "";
      const terms = parseFloat(attr["mrr-terms"]) || 0;
      const oneTimeRevenue = parseFloat(attr.price) || 0;
      const recurringRevenue =
        (parseFloat(attr["recurring-price"]) || 0) * terms;
      const totalRevenue = parseFloat(attr["total-revenue"]) || 0;
      const oneTimeCost = parseFloat(attr.cost) || 0;
      const recurringCost = (parseFloat(attr["recurring-cost"]) || 0) * terms;
      const profit = parseFloat(attr["profit"]) || 0;
      const margin = totalRevenue > 0 ? (profit / totalRevenue) * 100 : 0;

      return {
        id: String(quote.id),
        vendorName,
        description: attr.description || "",
        oneTimeRevenue,
        recurringRevenue,
        totalRevenue,
        oneTimeCost,
        recurringCost,
        profit,
        margin,
      };
    }) || [];

  return (
    <Show
      services={services}
      currencyUnit={currencyUnit}
      isLoading={quotesLoading}
    />
  );
};

export default ThirdPartyPricingV2Mspa;
